import { useCallback } from 'react';
import {
  useGetSchedulingSettingsQuery,
  useUpdateSchedulingSettingsMutation,
} from '../endpoints/scheduling.endpoints';
import type {
  SchedulingSettingsField,
  SchedulingSettingsFieldErrors,
  UpdateSchedulingSettingsParams,
} from '../types/scheduling.types';

type SaveResult =
  | { ok: true }
  | { ok: false; fieldErrors?: SchedulingSettingsFieldErrors; message?: string };

function toApiTime(hhmm: string): string {
  return hhmm.length === 5 ? `${hhmm}:00` : hhmm;
}

function firstString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === 'string') {
    return value[0];
  }
  return undefined;
}

function parseFieldErrors(responseData?: Record<string, unknown>): SchedulingSettingsFieldErrors {
  if (!responseData) {
    return {};
  }

  const fieldErrors: SchedulingSettingsFieldErrors = {};
  const fields: SchedulingSettingsField[] = [
    'booking_start_time',
    'booking_end_time',
    'booking_timezone',
  ];

  for (const field of fields) {
    const message = firstString(responseData[field]);
    if (message) {
      fieldErrors[field] = message;
    }
  }

  const nonFieldError = firstString(responseData.non_field_errors);
  if (nonFieldError && !fieldErrors.booking_end_time) {
    fieldErrors.booking_end_time = nonFieldError;
  }

  const detail = firstString(responseData.detail);
  if (detail && Object.keys(fieldErrors).length === 0) {
    fieldErrors.booking_end_time = detail;
  }

  return fieldErrors;
}

export function useSchedulingSettings() {
  const { data, isLoading, isError } = useGetSchedulingSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] = useUpdateSchedulingSettingsMutation();

  const saveBookingWindow = useCallback(async(
    bookingStartTime: string,
    bookingEndTime: string,
    bookingTimezone: string,
  ): Promise<SaveResult> => {
    const payload: UpdateSchedulingSettingsParams = {
      booking_start_time: toApiTime(bookingStartTime),
      booking_end_time: toApiTime(bookingEndTime),
      booking_timezone: bookingTimezone,
    };

    try {
      await updateSettings(payload).unwrap();
      return { ok: true };
    } catch (error: unknown) {
      const responseData = (error as { responseData?: Record<string, unknown> })?.responseData;
      const fieldErrors = parseFieldErrors(responseData);

      if (Object.keys(fieldErrors).length > 0) {
        return { ok: false, fieldErrors };
      }

      return {
        ok: false,
        message: 'Failed to save booking window.',
      };
    }
  }, [updateSettings]);

  return {
    settings: data,
    isLoading,
    isError,
    isSaving,
    saveBookingWindow,
  };
}
